import { isInboxItem } from '@seen/core/models/inbox'
import { useCallback } from 'react'
import { toast } from 'sonner'
import type { InboxNotificationItemFilterFn } from '#src/hooks/inbox/filter.ts'
import { archiveInboxItemsVerified } from '#src/hooks/inbox/inboxLiveRemoval.ts'
import { useInboxDeferredArchive } from '#src/hooks/inbox/useInboxDeferredArchive.ts'
import { useKnockFeedClient } from '#src/hooks/useKnockFeedClient.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'

/* Reads the Knock feed snapshot at call time (via feedClient.store.getState)
   rather than subscribing via useNotificationItems. This hook is consumed
   inside per-row components like TaskRow, so subscribing would force every
   row to re-render on any Knock store mutation. */
export function useMarkRelatedInboxItemsAsDone(options?: {
  silent?: boolean
}): (filterFn: InboxNotificationItemFilterFn) => void {
  const feedClient = useKnockFeedClient()
  const archiveOrDefer = useInboxDeferredArchive()
  return useCallback(
    (filterFn: InboxNotificationItemFilterFn) => {
      const { items } = feedClient.store.getState()
      const itemsToMarkAsDone = items.filter(
        (item): item is NotificationItem =>
          !item.archived_at &&
          isInboxItem(item.data) &&
          filterFn(item as NotificationItem)
      )
      if (itemsToMarkAsDone.length) {
        if (archiveOrDefer) {
          archiveOrDefer(itemsToMarkAsDone)
        } else {
          void archiveInboxItemsVerified(feedClient, itemsToMarkAsDone)
        }
        if (!options?.silent) {
          toast.success(
            itemsToMarkAsDone.length > 1
              ? `${itemsToMarkAsDone.length} matching inbox items marked as done`
              : 'Matching inbox item marked as done'
          )
        }
      }
    },
    [feedClient, options?.silent, archiveOrDefer]
  )
}
